import { ScoreEntry } from '@/types/cricket';
import { 
  GroupedBalls, 
  isWicketBall, 
  isExtraBall, 
  isBoundaryBall, 
  isOverthrowBall, 
  isRegularRunBall, 
  isDotBall 
} from './types';

export const BALL_COLORS = {
  NOT_BOWLED: '#E5E7EB',
  MULTI_SCORE: '#6366F1',
  WICKET: '#EF4444',
  NO_BALL_WIDE: '#F59E0B',
  BYE_LEG_BYE: '#F97316',
  OVERTHROW: '#8B5CF6',
  FOUR: '#22C55E',
  SIX: '#10B981',
  REGULAR_RUN: '#3B82F6',
  DOT_BALL: '#9CA3AF',
  REVIEW_NOT_OUT: '#EC4899', // Pink color for "Not Out" reviews
} as const;

// Helper function to detect review events
export const isReviewEvent = (ball: ScoreEntry): boolean => {
  return ball.description?.includes('Review') && ball.description?.includes('NOT OUT') || false;
};

export const getBallColor = (ball: ScoreEntry | undefined): string => {
  if (!ball) return BALL_COLORS.NOT_BOWLED; // Not bowled yet
  
  // Check for review events first
  if (isReviewEvent(ball)) return BALL_COLORS.REVIEW_NOT_OUT;
  
  // Determine if multiple score types are present (runs, boundary, extras, overthrows)
  const hasRuns = (ball?.runs || 0) > 0;
  const hasBonus = (ball?.bonus_boundary || 0) > 0;
  const hasExtras = (ball?.extras || 0) > 0;
  const hasOverthrows = (ball?.overthrows || 0) > 0;
  const scoreTypeCount = [hasRuns, hasBonus, hasExtras, hasOverthrows].filter(Boolean).length;
  if (scoreTypeCount >= 2) {
    return BALL_COLORS.MULTI_SCORE;
  }
  
  if (isWicketBall(ball)) return BALL_COLORS.WICKET;
  
  if (isExtraBall(ball)) {
    if (ball.extraType === 'noball' || ball.extraType === 'wide') return BALL_COLORS.NO_BALL_WIDE;
    if (ball.extraType === 'bye' || ball.extraType === 'legbye') return BALL_COLORS.BYE_LEG_BYE;
  }
  
  if (isOverthrowBall(ball)) return BALL_COLORS.OVERTHROW;
  
  if (isBoundaryBall(ball)) {
    if (ball.fours === 1 || ball.bonus_boundary === 4) return BALL_COLORS.FOUR;
    if (ball.sixes === 1 || ball.bonus_boundary === 6) return BALL_COLORS.SIX;
  }
  
  if (isRegularRunBall(ball)) return BALL_COLORS.REGULAR_RUN;
  
  if (isDotBall(ball)) return BALL_COLORS.DOT_BALL;
  
  return BALL_COLORS.NOT_BOWLED;
};

export const getBallDisplay = (ball: ScoreEntry | undefined): string => {
  if (!ball) return '';
  
  // Check for review events first
  if (isReviewEvent(ball)) return 'R';
  
  if (isWicketBall(ball)) return 'W';
  
  if (isExtraBall(ball)) {
    if (ball.extraType === 'wide') return 'Wd';
    if (ball.extraType === 'noball') return 'Nb';
    if (ball.extraType === 'bye') return 'B';
    if (ball.extraType === 'legbye') return 'Lb';
  }
  
  // If multiple score types (runs, boundary, overthrows, etc.), show total as '+N'
  const hasRuns = (ball.runs || 0) > 0;
  const hasBonus = (ball.bonus_boundary || 0) > 0;
  const hasOverthrows = (ball.overthrows || 0) > 0;
  const scoreTypeCount = [hasRuns, hasBonus, hasOverthrows].filter(Boolean).length;
  if (scoreTypeCount >= 2) {
    const total = (ball.runs || 0) + (ball.bonus_boundary || 0) + (ball.overthrows || 0);
    return `+${total}`;
  }
  
  // If only overthrows, show just the number (no '+')
  if (isOverthrowBall(ball) && !hasRuns && !hasBonus) return `${ball.overthrows}`;
  
  if (hasRuns && hasBonus) {
    return `${ball.runs}+${ball.bonus_boundary}`;
  }
  
  if (isBoundaryBall(ball)) {
    if (ball.fours === 1 || ball.bonus_boundary === 4) return '4';
    if (ball.sixes === 1 || ball.bonus_boundary === 6) return '6';
  }
  
  if (isRegularRunBall(ball)) return ball.runs.toString();
  
  return '0';
};

export const groupBallsByOver = (scoreHistory: ScoreEntry[]): GroupedBalls => {
  return scoreHistory.reduce((acc, ball) => {
    const key = `${ball.battingTeamId}-${ball.overNumber}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(ball);
    return acc;
  }, {} as GroupedBalls);
};

export const sortBallsInOver = (balls: ScoreEntry[]): ScoreEntry[] => {
  return [...balls].sort((a, b) => {
    // First sort by ball number
    if (a.ballNumber !== b.ballNumber) {
      return a.ballNumber - b.ballNumber;
    }
    // Then sort by timestamp for extra balls
    return (a.timestamp || 0) - (b.timestamp || 0);
  });
};

export const isPureExtraBall = (ball: ScoreEntry): boolean => {
  // A pure extra ball has extra runs but no other runs, boundaries, wickets, or overthrows
  return isExtraBall(ball) &&
         (ball.runs || 0) === 0 &&
         (ball.fours || 0) === 0 &&
         (ball.sixes || 0) === 0 &&
         (ball.overthrows || 0) === 0 &&
         !ball.wicket;
}; 